import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LoginCredentials } from "@snipe/core";
import { useSession } from "@/auth/session";
import { SnipeMark } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/field";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { user, loading, signIn, signInError, signingIn } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (loading) {
    return (
      <p role="status" className="p-6 text-sm text-muted-foreground">
        Checking your session…
      </p>
    );
  }

  if (user !== null) {
    const state = location.state as LocationState | null;
    return <Navigate to={state?.from ?? "/"} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The looser LOGIN schema, not `Credentials`: the registration floor would
    // reject a customer whose password predates it, with a message telling
    // them their own password is invalid.
    const parsed = LoginCredentials.safeParse({ email, password });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join("."), issue.message]),
        ),
      );
      return;
    }
    setFieldErrors({});
    // A refused sign-in is an expected answer, not an exception to escape:
    // `signInError` below is what renders it, and the page stays put.
    await signIn(parsed.data).catch(() => undefined);
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2.5">
            <SnipeMark size={28} />
            Snipe ops
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
            <Field label="Email" htmlFor="email" error={fieldErrors["email"]}>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                aria-invalid={fieldErrors["email"] !== undefined}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field label="Password" htmlFor="password" error={fieldErrors["password"]}>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                aria-invalid={fieldErrors["password"] !== undefined}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {signInError !== null ? (
              <p role="alert" className="text-sm font-medium text-danger">
                {signInError.message}
              </p>
            ) : null}

            <Button type="submit" disabled={signingIn}>
              {signingIn ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
